import React, { useState, useEffect, useCallback } from 'react';
import { maaserAPI } from '../services/api';
import { FiPlus, FiTrash2, FiX, FiPercent, FiCheckCircle, FiClock } from 'react-icons/fi';

/** שמות החודשים בעברית */
const HEBREW_MONTHS = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

/**
 * Maaser - דף תשלומי מעשר
 * מציג רשימת תשלומי מעשר, סיכום חודשי ואפשרות להוספה ומחיקה
 */
const Maaser = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    recipient: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  /**
   * טעינת נתוני הסיכום מהשרת
   */
  const fetchSummary = useCallback(async () => {
    try {
      const response = await maaserAPI.getSummary({
        month: selectedMonth,
        year: selectedYear,
      });
      setSummary(response.data);
    } catch (err) {
      console.error('שגיאה בטעינת נתוני סיכום:', err);
      setSummary(null);
    }
  }, [selectedMonth, selectedYear]);

  /**
   * טעינת רשימת תשלומי מעשר מהשרת
   */
  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await maaserAPI.getPayments({
        month: selectedMonth,
        year: selectedYear,
      });
      setPayments(response.data.payments || response.data || []);
    } catch (err) {
      console.error('שגיאה בטעינת תשלומים:', err);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  /** טעינה ראשונית ובכל שינוי חודש/שנה */
  useEffect(() => {
    fetchSummary();
    fetchPayments();
  }, [fetchSummary, fetchPayments]);

  /**
   * עיצוב מספר למטבע ישראלי
   * @param {number} value - הערך המספרי
   * @returns {string} מחרוזת מעוצבת עם סימן שקל
   */
  const formatCurrency = (value) => {
    if (value == null) return '₪0';
    return `₪${Number(value).toLocaleString('he-IL')}`;
  };

  /**
   * עיצוב תאריך לפורמט ישראלי
   * @param {string} dateStr - מחרוזת תאריך
   * @returns {string} תאריך מעוצב DD/MM/YYYY
   */
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('he-IL');
  };

  /**
   * עדכון שדה בטופס
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * פתיחת חלון הוספת תשלום
   */
  const openModal = () => {
    setFormData({
      amount: '',
      recipient: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
    setError('');
    setShowModal(true);
  };

  /**
   * סגירת חלון הוספת תשלום
   */
  const closeModal = () => {
    setShowModal(false);
    setError('');
  };

  /**
   * שליחת טופס הוספת תשלום מעשר
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.amount || Number(formData.amount) <= 0) {
      setError('יש להזין סכום חיובי.');
      return;
    }

    try {
      await maaserAPI.addPayment({
        amount: Number(formData.amount),
        recipient: formData.recipient,
        description: formData.description,
        date: formData.date,
      });
      closeModal();
      fetchSummary();
      fetchPayments();
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'שגיאה בהוספת תשלום. אנא נסה שנית.'
      );
    }
  };

  /**
   * מחיקת תשלום מעשר עם אישור
   * @param {number|string} id - מזהה התשלום
   */
  const handleDelete = async (id) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק תשלום זה?')) {
      return;
    }

    try {
      await maaserAPI.deletePayment(id);
      fetchSummary();
      fetchPayments();
    } catch (err) {
      console.error('שגיאה במחיקת תשלום:', err);
    }
  };

  /**
   * תצוגת טעינה - ספינר
   */
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>טוען נתונים...</p>
      </div>
    );
  }

  return (
    <div className="page">
      {/* כותרת הדף */}
      <div className="page-header">
        <h1>תשלומי מעשר</h1>
        <p className="page-subtitle">מעקב אחר תשלומי המעשר שלך</p>
      </div>

      {/* סרגל סינון - בחירת חודש, שנה וכפתור הוספה */}
      <div className="filter-bar">
        <select
          className="form-control"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
        >
          {HEBREW_MONTHS.map((month, index) => (
            <option key={index + 1} value={index + 1}>
              {month}
            </option>
          ))}
        </select>
        <select
          className="form-control"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {Array.from({ length: 11 }, (_, i) => 2020 + i).map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        <button className="btn btn-success" onClick={openModal}>
          <FiPlus />
          הוסף תשלום מעשר
        </button>
      </div>

      {/* כרטיסי סיכום מעשר */}
      {summary && (
        <div className="summary-grid">
          {/* מעשר נדרש */}
          <div className="summary-card">
            <div className="summary-card-icon maaser">
              <FiPercent />
            </div>
            <div className="summary-card-content">
              <h3>מעשר נדרש</h3>
              <span className="value">
                {formatCurrency(summary.maaser_required)}
              </span>
            </div>
          </div>

          {/* מעשר ששולם */}
          <div className="summary-card">
            <div className="summary-card-icon paid">
              <FiCheckCircle />
            </div>
            <div className="summary-card-content">
              <h3>מעשר ששולם</h3>
              <span className="value positive">
                {formatCurrency(summary.maaser_paid)}
              </span>
            </div>
          </div>

          {/* נותר לתשלום */}
          <div className="summary-card">
            <div className="summary-card-icon remaining">
              <FiClock />
            </div>
            <div className="summary-card-content">
              <h3>נותר לתשלום</h3>
              <span className="value negative">
                {formatCurrency(summary.maaser_remaining)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* טבלת תשלומים */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">רשימת תשלומים</h3>
        </div>

        {payments.length === 0 ? (
          <div className="empty-state">
            <p>אין תשלומי מעשר לתקופה שנבחרה</p>
          </div>
        ) : (
          <>
            {/* תצוגת טבלה - דסקטופ בלבד */}
            <div className="desktop-only">
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>תאריך</th>
                      <th>נמען</th>
                      <th>תיאור</th>
                      <th>סכום</th>
                      <th>פעולות</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id}>
                        <td>{formatDate(payment.date)}</td>
                        <td>{payment.recipient}</td>
                        <td>{payment.description}</td>
                        <td>{formatCurrency(payment.amount)}</td>
                        <td>
                          <div className="actions">
                            <button
                              className="btn btn-danger btn-sm btn-icon"
                              onClick={() => handleDelete(payment.id)}
                              title="מחק תשלום"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* תצוגת כרטיסים - מובייל בלבד */}
            <div className="mobile-only">
              <div className="mobile-list">
                {payments.map((payment) => (
                  <div className="mobile-card" key={payment.id}>
                    <div className="mobile-card-row">
                      <span className="mobile-card-meta">{payment.recipient || 'לא צוין'}</span>
                      <span className="mobile-card-date">{formatDate(payment.date)}</span>
                    </div>
                    {payment.description && <p className="mobile-card-desc">{payment.description}</p>}
                    <div className="mobile-card-row">
                      <span className="mobile-card-amount">{formatCurrency(payment.amount)}</span>
                      <div className="mobile-card-actions">
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(payment.id)} title="מחק תשלום"><FiTrash2 /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* חלון הוספת תשלום מעשר */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>הוספת תשלום מעשר</h3>
              <button
                className="btn btn-icon btn-outline"
                onClick={closeModal}
              >
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}

                <div className="form-group">
                  <label htmlFor="amount">סכום</label>
                  <input
                    id="amount"
                    type="number"
                    name="amount"
                    className="form-control"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="הזן סכום"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="recipient">נמען / ארגון</label>
                  <input
                    id="recipient"
                    type="text"
                    name="recipient"
                    className="form-control"
                    value={formData.recipient}
                    onChange={handleChange}
                    placeholder="הזן שם נמען או ארגון"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">תיאור</label>
                  <input
                    id="description"
                    type="text"
                    name="description"
                    className="form-control"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="הזן תיאור (אופציונלי)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="date">תאריך</label>
                  <input
                    id="date"
                    type="date"
                    name="date"
                    className="form-control"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="submit" className="btn btn-success">
                  <FiPlus />
                  הוסף תשלום
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={closeModal}
                >
                  ביטול
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Maaser;
